import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

import SearchBar from './components/SearchBar';
import CategoryGrid from './components/CategoryGrid';
import FeaturedArticles from './components/FeaturedArticles';
import VideoTutorials from './components/VideoTutorials';
import TroubleshootingSection from './components/TroubleshootingSection';
import CommunityForum from './components/CommunityForum';
import LiveChatWidget from './components/LiveChatWidget';
import FeedbackSystem from './components/FeedbackSystem';

const HelpCenter = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [isChatOpen, setIsChatOpen] = useState(false);

  // Mock data for help center
  const helpCategories = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      description: 'Learn the basics of using ShaadiNest',
      icon: 'Play',
      articleCount: 12,
      color: 'primary'
    },
    {
      id: 'booking-vendors',
      title: 'Booking Vendors',
      description: 'How to find and book the perfect vendors',
      icon: 'Calendar',
      articleCount: 18,
      color: 'secondary'
    },
    {
      id: 'account-management',
      title: 'Account Management',
      description: 'Manage your profile and settings',
      icon: 'User',
      articleCount: 15,
      color: 'success'
    },
    {
      id: 'vendor-registration',
      title: 'Vendor Registration',
      description: 'For vendors joining our platform',
      icon: 'UserPlus',
      articleCount: 22,
      color: 'warning'
    },
    {
      id: 'billing-payments',
      title: 'Billing & Payments',
      description: 'Payment processing and billing questions',
      icon: 'CreditCard',
      articleCount: 14,
      color: 'info'
    },
    {
      id: 'technical-support',
      title: 'Technical Support',
      description: 'Technical issues and troubleshooting',
      icon: 'Settings',
      articleCount: 20,
      color: 'danger'
    }
  ];

  const featuredArticles = [
    {
      id: 1,
      title: 'How to Choose the Right Cultural Vendor',
      excerpt: 'Learn how to identify vendors who understand your cultural traditions and can deliver authentic experiences.',
      category: 'getting-started',
      readTime: '5 min read',
      views: 2847,
      helpful: 156,
      lastUpdated: '2024-01-15'
    },
    {
      id: 2,
      title: 'South Asian Wedding Booking Etiquette',
      excerpt: 'Understanding proper communication and booking protocols when working with wedding vendors.',
      category: 'booking-vendors',
      readTime: '7 min read',
      views: 1923,
      helpful: 142,
      lastUpdated: '2024-01-10'
    },
    {
      id: 3,
      title: 'Payment Processing for Wedding Services',
      excerpt: 'Everything you need to know about secure payments, deposits, and payment schedules.',
      category: 'billing-payments',
      readTime: '6 min read',
      views: 1654,
      helpful: 128,
      lastUpdated: '2024-01-08'
    },
    {
      id: 4,
      title: 'Vendor Registration Step-by-Step Guide',
      excerpt: 'Complete walkthrough for vendors joining the ShaadiNest marketplace.',
      category: 'vendor-registration',
      readTime: '10 min read',
      views: 3124,
      helpful: 201,
      lastUpdated: '2024-01-12'
    },
    {
      id: 5,
      title: 'Creating Professional Invoice Templates',
      excerpt: 'Learn how to create detailed invoices that meet cultural and business requirements.',
      category: 'vendor-registration',
      readTime: '8 min read',
      views: 987,
      helpful: 89,
      lastUpdated: '2024-01-05'
    },
    {
      id: 6,
      title: 'Mobile App Navigation Tips',
      excerpt: 'Making the most of the ShaadiNest mobile experience for on-the-go planning.',
      category: 'getting-started',
      readTime: '4 min read',
      views: 1543,
      helpful: 112,
      lastUpdated: '2024-01-14'
    }
  ];

  const videoTutorials = [
    {
      id: 1,
      title: 'Platform Navigation for Couples',
      description: 'Complete tour of the ShaadiNest platform',
      duration: '12:30',
      views: 15420,
      thumbnail: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    },
    {
      id: 2,
      title: 'Cultural Context in Vendor Selection',
      description: 'Understanding vendor specializations',
      duration: '8:45',
      views: 9876,
      thumbnail: 'https://images.unsplash.com/photo-1587271407850-8d438ca9fdf2?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    },
    {
      id: 3,
      title: 'South Asian Wedding Planning Timeline',
      description: 'Traditional planning schedules and milestones',
      duration: '15:20',
      views: 12543,
      thumbnail: 'https://images.unsplash.com/photo-1519741497674-611481863552?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    }
  ];

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'hi', name: 'Hindi', flag: '🇮🇳' },
    { code: 'pa', name: 'Punjabi', flag: '🇮🇳' },
    { code: 'gu', name: 'Gujarati', flag: '🇮🇳' },
    { code: 'ta', name: 'Tamil', flag: '🇮🇳' },
    { code: 'te', name: 'Telugu', flag: '🇮🇳' }
  ];

  // Search functionality with debouncing
  const handleSearch = async (query) => {
    setSearchQuery(query);
    
    if (!query || query.trim().length === 0) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    
    try {
      // Search through featured articles
      const results = featuredArticles.filter(article =>
        article?.title?.toLowerCase().includes(query?.toLowerCase()) ||
        article?.excerpt?.toLowerCase().includes(query?.toLowerCase()) ||
        article?.category?.toLowerCase().includes(query?.toLowerCase())
      );
      
      setSearchResults(results);
    } catch (error) {
      console.error('Error searching help articles:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Handle category selection
  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
    const filteredResults = featuredArticles.filter(article => 
      article?.category === categoryId
    );
    setSearchResults(filteredResults);
  };

  // Handle article click
  const handleArticleClick = (articleId) => {
    // Navigate to article detail page
    navigate(`/help-center/article/${articleId}`);
  };

  // Handle language change
  const handleLanguageChange = (langCode) => {
    setSelectedLanguage(langCode);
    // In real app, this would trigger content translation
  };

  // Get URL parameters on load
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const category = params.get('category');
    const search = params.get('search');
    
    if (category) {
      handleCategorySelect(category);
    }
    if (search) {
      handleSearch(search);
    }
  }, [location]);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Help Center - ShaadiNest | Get Support & Learn</title>
        <meta 
          name="description" 
          content="Find answers to your questions about ShaadiNest. Browse articles, watch tutorials, and get help with wedding planning and vendor booking." 
        />
        <meta name="keywords" content="help, support, wedding planning, vendor booking, ShaadiNest guide" />
      </Helmet>

      <Header />

      <div className="pt-16">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary-50 to-secondary-50 py-16 lg:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="font-heading font-bold text-4xl sm:text-5xl text-text-primary mb-6">
              How Can We Help You?
            </h1>
            <p className="text-xl text-text-secondary mb-8">
              Find answers, learn tips, and get support for your wedding planning journey
            </p>
            
            {/* Search Bar */}
            <SearchBar
              onSearch={handleSearch}
              isSearching={isSearching}
              placeholder="Search for help articles, guides, or tutorials..."
            />
            
            {/* Language Selector */}
            <div className="flex items-center justify-center space-x-4 mt-6">
              <span className="text-text-secondary">Available in:</span>
              <div className="flex items-center space-x-2">
                {languages?.map((lang) => (
                  <button
                    key={lang?.code}
                    onClick={() => handleLanguageChange(lang?.code)}
                    className={`flex items-center space-x-1 px-3 py-1 rounded-full text-sm transition-colors ${
                      selectedLanguage === lang?.code
                        ? 'bg-primary text-white' :'bg-white text-text-secondary hover:bg-surface-100'
                    }`}
                  >
                    <span>{lang?.flag}</span>
                    <span>{lang?.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Search Results */}
        {(searchResults?.length > 0 || isSearching) && (
          <section className="py-12 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading font-bold text-2xl text-text-primary">
                  {isSearching ? 'Searching...' : `Search Results (${searchResults?.length})`}
                </h2>
                {searchResults?.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSearchResults([]);
                      setSearchQuery('');
                      setSelectedCategory('');
                    }}
                  >
                    Clear Results
                  </Button>
                )}
              </div>
              
              {isSearching ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[...Array(6)]?.map((_, i) => (
                    <div key={i} className="bg-surface-100 rounded-lg p-6 animate-pulse">
                      <div className="h-4 bg-surface-200 rounded mb-4"></div>
                      <div className="h-3 bg-surface-200 rounded mb-2"></div>
                      <div className="h-3 bg-surface-200 rounded w-2/3"></div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {searchResults?.map((article) => (
                    <div
                      key={article?.id}
                      onClick={() => handleArticleClick(article?.id)}
                      className="bg-white border border-border rounded-lg p-6 cursor-pointer hover:shadow-medium transition-shadow"
                    >
                      <h3 className="font-heading font-semibold text-lg text-text-primary mb-2">
                        {article?.title}
                      </h3>
                      <p className="text-text-secondary text-sm mb-4">
                        {article?.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-text-muted">
                        <span>{article?.readTime}</span>
                        <span>{article?.views} views</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}

        {/* Main Categories */}
        {!searchResults?.length && (
          <>
            <CategoryGrid
              categories={helpCategories}
              onCategorySelect={handleCategorySelect}
            />
            
            {/* Featured Articles */}
            <FeaturedArticles
              articles={featuredArticles}
              onArticleClick={handleArticleClick}
            />
            
            {/* Video Tutorials */}
            <VideoTutorials
              videos={videoTutorials}
              onVideoClick={(videoId) => console.log('Play video:', videoId)}
            />
            
            {/* Troubleshooting */}
            <TroubleshootingSection />
            
            {/* Community Forum */}
            <CommunityForum />
          </>
        )}

        {/* Contact Support */}
        <section className="py-16 bg-surface-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl text-text-primary mb-6">
              Still Need Help?
            </h2>
            <p className="text-lg text-text-secondary mb-8">
              Can't find what you're looking for? Our support team is here to help
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg p-6 text-center">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="MessageCircle" size={24} className="text-primary" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-text-primary mb-2">
                  Live Chat
                </h3>
                <p className="text-text-secondary text-sm mb-4">
                  Chat with our support team in real-time
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsChatOpen(true)}
                >
                  Start Chat
                </Button>
              </div>
              
              <div className="bg-white rounded-lg p-6 text-center">
                <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Mail" size={24} className="text-secondary" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-text-primary mb-2">
                  Email Support
                </h3>
                <p className="text-text-secondary text-sm mb-4">
                  Get detailed help via email
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/contact-us')}
                >
                  Send Email
                </Button>
              </div>
              
              <div className="bg-white rounded-lg p-6 text-center">
                <div className="w-16 h-16 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Phone" size={24} className="text-success" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-text-primary mb-2">
                  Phone Support
                </h3>
                <p className="text-text-secondary text-sm mb-4">
                  Call us during business hours
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open('tel:+15551234567')}
                >
                  (555) 123-4567
                </Button>
              </div>
            </div>
            
            <div className="mt-8 p-4 bg-info-50 rounded-lg">
              <p className="text-info text-sm">
                <Icon name="Clock" size={16} className="inline mr-2" />
                Support Hours: Monday - Friday, 9 AM - 6 PM PST
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* Live Chat Widget */}
      <LiveChatWidget
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
      />

      {/* Feedback System */}
      <FeedbackSystem />

      <Footer />
    </div>
  );
};

export default HelpCenter;